import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

export default function PaymentResult({ cancelled = false }: { cancelled?: boolean }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { reload } = useAuth();
  const [state, setState] = useState<"checking" | "paid" | "failed">(cancelled ? "failed" : "checking");
  const [error, setError] = useState("");

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          reload("vendor");
          return;
        }
        if (data.status === "expired") return setState("failed");
      } catch (e) {
        console.error("Payment status check failed", e);
        setError(errMsg(e));
      }
      if (++tries < 15) setTimeout(poll, 2000);
      else setState("failed");
    };
    poll();
  }, [sessionId, cancelled, reload]);

  return (
    <div className="grid min-h-screen place-items-center bg-background p-6 text-foreground">
      <div className="max-w-sm space-y-4 text-center" data-testid="payment-result">
        {state === "checking" && <Loader2 className="mx-auto h-10 w-10 animate-spin text-emerald-400" />}
        {state === "paid" && <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />}
        {state === "failed" && error && <p className="text-sm text-muted-foreground" data-testid="payment-result-error">{error}. If you were charged, your plan will update shortly — contact support if it doesn't.</p>}
        {state === "failed" && <XCircle className="mx-auto h-12 w-12 text-rose-400" />}
        <h1 className="text-2xl font-bold" data-testid="payment-result-title">
          {state === "checking" ? "Confirming your payment..." : state === "paid" ? "Subscription active" : "Payment not completed"}
        </h1>
        <Link to="/dashboard/account" data-testid="payment-back-button"><Button className="hard-shadow rounded-sm">Back to account</Button></Link>
      </div>
    </div>
  );
}
